// PRODUCTION — admin flow editor
import React, { Component, ErrorInfo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
// AppLogger is instance-based; use console.error for error boundary since
// we can't use hooks in class components

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class FlowEditorErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('FlowEditorErrorBoundary caught error', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div css={tw`flex flex-col items-center justify-center gap-4 p-16`}>
          <div css={tw`w-12 h-12 rounded-xl bg-critical-50 flex items-center justify-center`}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              css={tw`text-critical-500`}
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <Typography variant="h4" css={tw`text-shades-900`}>
            Something went wrong
          </Typography>
          <Typography variant="body2" css={tw`text-shades-500 text-center max-w-md`}>
            An error occurred while loading the flow editor. Please try again.
          </Typography>
          {this.state.error && (
            <Typography variant="subtitle2" css={tw`text-shades-400 font-mono text-xs`}>
              {this.state.error.message}
            </Typography>
          )}
          <Button onClick={this.handleReset} color="primary" size="medium">
            Try Again
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}
